import React from "react";
import { useToast } from "@chakra-ui/react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { validateDomain, validateEmail } from "data/mockData";
import { getTenantById, updateTenant, fetchTenantReferralSources } from "action/tenantAction";
import { fetchPackages, transformPackagesData } from "action/packageAction";
import PackageSelectList from "components/PackageSelectList";

/* ── Icons ── */
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
  </svg>
);
const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const STEP_LABELS = ["Tenant Info", "PIC Details", "Contacts", "Package"];

function EditTenant() {
  const { t } = useTranslation();
  const STEPS = STEP_LABELS.map((label, i) => ({ num: i + 1, label: t(label) }));

  React.useEffect(() => { document.title = t("Edit Tenant") + " | LMS Tenant"; }, []);

  const { id } = useParams();
  const history = useHistory();
  const toast = useToast();

  const [step, setStep] = React.useState(1);
  const [tenant, setTenant] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [packages, setPackages] = React.useState([]);
  const [packagesLoading, setPackagesLoading] = React.useState(false);
  const [referralSources, setReferralSources] = React.useState([]);
  const [referralSourcesLoading, setReferralSourcesLoading] = React.useState(false);

  const [tenantInfo, setTenantInfo] = React.useState({ namaTenant: "", domainUnik: "", alamat: "", status: "Active", remark: "" });
  const [tenantInfoErrors, setTenantInfoErrors] = React.useState({});

  const [pics, setPics] = React.useState([{ nama: "", nomorTelepon: "", email: "" }]);
  const [picErrors, setPicErrors] = React.useState([]);

  const [contacts, setContacts] = React.useState([{ name: "", email: "", phone: "" }]);
  const [contactErrors, setContactErrors] = React.useState([]);
  const [referralSource, setReferralSource] = React.useState("");
  const [referralSourceError, setReferralSourceError] = React.useState("");

  const [subscription, setSubscription] = React.useState({
    packageId: null, customPackageName: "", customPackagePrice: 0,
    installationFee: 0, discountPercent: 0, tanggalMulai: "",
    tanggalBerakhir: "", gracePeriod: 0, jumlahStudent: 1, additionalAttachment: null,
  });
  const [subscriptionErrors, setSubscriptionErrors] = React.useState({});

  React.useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const tenantResult = await getTenantById(id);
        if (tenantResult.success) {
          const t = tenantResult.data;
          setTenant(t);
          setTenantInfo({ namaTenant: t.namaTenant || "", domainUnik: t.domainUnik || "", alamat: t.alamat || "", status: t.status || "Active", remark: t.remark || "" });
          setReferralSource(t.referralSourceId || "");
          setPics(t.pics?.length > 0 ? t.pics : [{ nama: "", nomorTelepon: "", email: "" }]);
          setContacts(t.invitations?.length > 0 ? t.invitations.map((inv) => ({ name: inv.name || "", email: inv.email || "", phone: inv.phone || "" })) : [{ name: "", email: "", phone: "" }]);
          const isCustom = !t.package?.id || t.id_package === 0;
          setSubscription({
            packageId: isCustom ? null : t.package?.id,
            customPackageName: isCustom ? t.package?.name || "Custom Package" : "",
            customPackagePrice: t.harga_package || 0,
            installationFee: t.implementation_cost || 0,
            discountPercent: t.diskon || 0,
            tanggalMulai: t.tanggalMulai || "",
            tanggalBerakhir: t.tanggalBerakhir || "",
            gracePeriod: t.gracePeriod || 0,
            jumlahStudent: t.jumlah_student > 0 ? t.jumlah_student : 1,
            additionalAttachment: null,
          });
        } else {
          toast({ title: t("Error"), description: tenantResult.message || t("Failed to load tenant data"), status: "error", duration: 3000, isClosable: true });
        }

        setPackagesLoading(true);
        const pkgResult = await fetchPackages({ per_page: 50 });
        if (pkgResult.success) setPackages(transformPackagesData(pkgResult.data));

        setReferralSourcesLoading(true);
        const refResult = await fetchTenantReferralSources();
        if (refResult.success) setReferralSources(refResult.data);
      } catch {
        toast({ title: t("Error"), description: t("An error occurred while loading data"), status: "error", duration: 3000, isClosable: true });
      } finally {
        setLoading(false);
        setPackagesLoading(false);
        setReferralSourcesLoading(false);
      }
    };
    loadData();
  }, [id]);

  const selectedPackage = React.useMemo(() => {
    if (subscription.packageId === null) return { id: null, name: subscription.customPackageName || "Custom Package", price: subscription.customPackagePrice, benefits: [] };
    return packages.find((p) => p.id === subscription.packageId);
  }, [subscription.packageId, subscription.customPackageName, subscription.customPackagePrice, packages]);

  const basePrice = React.useMemo(() => selectedPackage?.price || 0, [selectedPackage]);

  const durationMonths = React.useMemo(() => {
    if (!subscription.tanggalMulai || !subscription.tanggalBerakhir) return 1;
    const start = new Date(subscription.tanggalMulai);
    const end = new Date(subscription.tanggalBerakhir);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    return Math.max(months, 1);
  }, [subscription.tanggalMulai, subscription.tanggalBerakhir]);

  const discountedPricePerMonth = React.useMemo(() => {
    const d = Math.min(Math.max(Number(subscription.discountPercent) || 0, 0), 100);
    return Math.round(basePrice * (1 - d / 100));
  }, [basePrice, subscription.discountPercent]);

  const jumlahStudent = React.useMemo(() => Math.max(Number(subscription.jumlahStudent) || 1, 1), [subscription.jumlahStudent]);

  const discountedPrice = React.useMemo(() => discountedPricePerMonth * jumlahStudent * durationMonths, [discountedPricePerMonth, jumlahStudent, durationMonths]);
  const totalPrice = React.useMemo(() => discountedPrice + (Number(subscription.installationFee) || 0), [discountedPrice, subscription.installationFee]);

  const handleSubscriptionChange = (field, value) => {
    setSubscription((prev) => ({ ...prev, [field]: value }));
    if (subscriptionErrors[field]) setSubscriptionErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!tenantInfo.namaTenant.trim()) errs.namaTenant = t("Nama tenant wajib diisi");
    if (!tenantInfo.domainUnik.trim()) errs.domainUnik = t("Domain wajib diisi");
    else if (!validateDomain(tenantInfo.domainUnik)) errs.domainUnik = t("Format domain tidak valid");
    if (!tenantInfo.alamat.trim()) errs.alamat = t("Alamat wajib diisi");
    setTenantInfoErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs = pics.map(() => ({ nama: "", nomorTelepon: "", email: "" }));
    let ok = true;
    pics.forEach((p, i) => {
      if (!p.nama.trim()) { errs[i].nama = t("Nama PIC wajib"); ok = false; }
      if (!p.nomorTelepon.trim()) { errs[i].nomorTelepon = t("Nomor telepon wajib"); ok = false; }
      if (!p.email.trim()) { errs[i].email = t("Email wajib"); ok = false; }
      else if (!validateEmail(p.email)) { errs[i].email = t("Format email tidak valid"); ok = false; }
    });
    setPicErrors(errs);
    return ok;
  };

  const validateStep3 = () => {
    const errs = contacts.map(() => ({ name: "", email: "", phone: "" }));
    let ok = true;
    contacts.forEach((c, i) => {
      if (!c.name.trim()) { errs[i].name = t("Nama wajib"); ok = false; }
      if (!c.email.trim()) { errs[i].email = t("Email wajib"); ok = false; }
      else if (!validateEmail(c.email)) { errs[i].email = t("Email tidak valid"); ok = false; }
      if (!c.phone.trim()) { errs[i].phone = t("Nomor telp wajib"); ok = false; }
    });
    setContactErrors(errs);
    if (!referralSource) { setReferralSourceError(t("Referral source wajib")); ok = false; }
    else setReferralSourceError("");
    return ok;
  };

  const validateStep4 = () => {
    const errs = {};
    if (!subscription.tanggalMulai) errs.tanggalMulai = t("Tanggal mulai wajib diisi");
    if (!subscription.tanggalBerakhir) errs.tanggalBerakhir = t("Tanggal berakhir wajib diisi");
    if (subscription.tanggalMulai && subscription.tanggalBerakhir && new Date(subscription.tanggalMulai) >= new Date(subscription.tanggalBerakhir))
      errs.tanggalBerakhir = t("Tanggal berakhir harus setelah tanggal mulai");
    setSubscriptionErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const handleSave = async () => {
    if (!validateStep4()) return;
    setSaving(true);
    try {
      const tenantData = {
        name: tenantInfo.namaTenant, domain: tenantInfo.domainUnik, address: tenantInfo.alamat,
        status: tenantInfo.status.toLowerCase().replace(/ /g, '_'), remark: tenantInfo.remark || null,
        id_referral_source: referralSource,
        subscription_start_at: subscription.tanggalMulai, subscription_expired_at: subscription.tanggalBerakhir,
        grace_period_days: subscription.gracePeriod, additional_attachment: subscription.additionalAttachment,
        id_package: subscription.packageId === null ? 0 : subscription.packageId,
        harga_package: basePrice, diskon: subscription.discountPercent,
        implementation_cost: subscription.installationFee, jumlah_student: jumlahStudent, total: totalPrice,
        pics: pics.map((p) => ({ name: p.nama, email: p.email, phone: p.nomorTelepon })),
        user_invitation: contacts.filter((c) => c.name && c.email && c.phone).map((c) => ({ name: c.name, email: c.email, phone: c.phone })),
      };
      const result = await updateTenant(tenant.id, tenantData);
      if (result.success) {
        toast({ title: t("Success"), description: result.message || t("Tenant updated successfully"), status: "success", duration: 3000, isClosable: true });
        history.push(`/admin/tenant-detail/${tenant.id}`);
      } else {
        toast({ title: t("Error"), description: result.message || t("Failed to update tenant"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("An error occurred while updating tenant"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="lms-page lms-anim-fade">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80, gap: 16 }}>
          <div className="lms-spinner" style={{ width: 36, height: 36 }} />
          <span className="t-body lms-text-muted">{t("Loading...")}</span>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="lms-page lms-anim-fade">
        <div className="lms-empty">
          <div className="t-h3">{t("Tenant tidak ditemukan")}</div>
          <button className="lms-btn lms-btn-primary" style={{ marginTop: 16 }} onClick={() => history.goBack()}>{t("Go Back")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="lms-page lms-anim-fade">
      {/* Header */}
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span>{t("Apps")}</span><ChevronRightIcon /><span>{t("Admin")}</span><ChevronRightIcon />
            <span>{t("Master Tenant")}</span><ChevronRightIcon />
            <span className="lms-breadcrumb-current">{t("Edit Tenant")}</span>
          </div>
          <div className="t-display">{t("Edit Tenant")} — {tenant.namaTenant}</div>
        </div>
        <button className="lms-btn lms-btn-secondary" onClick={() => history.goBack()}>
          <ArrowLeftIcon /> {t("Back")}
        </button>
      </div>

      {/* Step Indicator */}
      <div className="lms-card" style={{ marginBottom: 20 }}>
        <div className="lms-card-body" style={{ padding: "20px 24px" }}>
          <div className="lms-wizard-stepper">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`lms-stepper-step ${step > s.num ? "done" : step === s.num ? "active" : ""}`}>
                  <div className="lms-stepper-num">{step > s.num ? <CheckIcon /> : s.num}</div>
                  <span className="lms-stepper-label">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="lms-stepper-bar" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="lms-card">
        <div className="lms-card-body">

          {/* Step 1: Tenant Information */}
          {step === 1 && (
            <div>
              <div className="t-h2" style={{ marginBottom: 20 }}>{t("Tenant Information")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div className={`lms-form-group ${tenantInfoErrors.namaTenant ? "has-error" : ""}`}>
                  <label className="lms-label">{t("Nama Tenant")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                  <input className="lms-input" value={tenantInfo.namaTenant} onChange={(e) => setTenantInfo({ ...tenantInfo, namaTenant: e.target.value })} placeholder={t("Masukkan nama tenant")} />
                  {tenantInfoErrors.namaTenant && <div className="lms-error-msg">{tenantInfoErrors.namaTenant}</div>}
                </div>
                <div className={`lms-form-group ${tenantInfoErrors.domainUnik ? "has-error" : ""}`}>
                  <label className="lms-label">{t("Domain")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                  <input className="lms-input" value={tenantInfo.domainUnik} onChange={(e) => setTenantInfo({ ...tenantInfo, domainUnik: e.target.value })} placeholder={t("example.com")} />
                  {tenantInfoErrors.domainUnik && <div className="lms-error-msg">{tenantInfoErrors.domainUnik}</div>}
                </div>
              </div>
              <div className={`lms-form-group ${tenantInfoErrors.alamat ? "has-error" : ""}`} style={{ marginBottom: 16 }}>
                <label className="lms-label">{t("Alamat")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <textarea className="lms-textarea" rows={3} value={tenantInfo.alamat} onChange={(e) => setTenantInfo({ ...tenantInfo, alamat: e.target.value })} placeholder={t("Masukkan alamat tenant")} />
                {tenantInfoErrors.alamat && <div className="lms-error-msg">{tenantInfoErrors.alamat}</div>}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div className="lms-form-group">
                  <label className="lms-label">{t("Status")}</label>
                  <select className="lms-select" value={tenantInfo.status} onChange={(e) => setTenantInfo({ ...tenantInfo, status: e.target.value })}>
                    <option value="Active">Active</option>
                    <option value="Non Active">Non Active</option>
                  </select>
                </div>
              </div>
              <div className="lms-form-group">
                <label className="lms-label">{t("Remark")} <span className="t-small lms-text-muted">{t("(opsional)")}</span></label>
                <textarea className="lms-textarea" rows={3} value={tenantInfo.remark} onChange={(e) => setTenantInfo({ ...tenantInfo, remark: e.target.value })} placeholder={t("Masukkan remark (opsional)")} />
              </div>
            </div>
          )}

          {/* Step 2: PIC Details */}
          {step === 2 && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div className="t-h2">{t("Person In Charge (PIC)")}</div>
                <button className="lms-btn lms-btn-secondary lms-btn-sm" onClick={() => setPics([...pics, { nama: "", nomorTelepon: "", email: "" }])}>
                  <PlusIcon /> {t("Tambah PIC")}
                </button>
              </div>
              {pics.map((pic, idx) => (
                <div key={idx} className="lms-repeater-card">
                  <div className="lms-repeater-card-header">
                    <span className="t-h3">PIC #{idx + 1}</span>
                    {pics.length > 1 && (
                      <button className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon" style={{ color: "var(--danger-500)" }} onClick={() => { setPics(pics.filter((_, i) => i !== idx)); setPicErrors(picErrors.filter((_, i) => i !== idx)); }}>
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                  <div className={`lms-form-group ${picErrors[idx]?.nama ? "has-error" : ""}`} style={{ marginBottom: 12 }}>
                    <label className="lms-label">{t("Nama PIC")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                    <input className="lms-input" value={pic.nama} onChange={(e) => { const n = [...pics]; n[idx].nama = e.target.value; setPics(n); }} placeholder={t("Masukkan nama PIC")} />
                    {picErrors[idx]?.nama && <div className="lms-error-msg">{picErrors[idx].nama}</div>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className={`lms-form-group ${picErrors[idx]?.nomorTelepon ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Nomor Telepon")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input className="lms-input" maxLength={13} value={pic.nomorTelepon} onChange={(e) => { const n = [...pics]; n[idx].nomorTelepon = e.target.value; setPics(n); }} placeholder={t("Masukkan nomor telepon")} />
                      {picErrors[idx]?.nomorTelepon && <div className="lms-error-msg">{picErrors[idx].nomorTelepon}</div>}
                    </div>
                    <div className={`lms-form-group ${picErrors[idx]?.email ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Email")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input className="lms-input" type="email" value={pic.email} onChange={(e) => { const n = [...pics]; n[idx].email = e.target.value; setPics(n); }} placeholder={t("Masukkan email PIC")} />
                      {picErrors[idx]?.email && <div className="lms-error-msg">{picErrors[idx].email}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 3: Contacts & Referral */}
          {step === 3 && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div className="t-h2">{t("Contacts & Referral Source")}</div>
                <button className="lms-btn lms-btn-secondary lms-btn-sm" onClick={() => setContacts([...contacts, { name: "", email: "", phone: "" }])}>
                  <PlusIcon /> {t("Tambah Contact")}
                </button>
              </div>
              <div className={`lms-form-group ${referralSourceError ? "has-error" : ""}`} style={{ marginBottom: 20 }}>
                <label className="lms-label">{t("Referral Source")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <select className="lms-select" value={referralSource} disabled={referralSourcesLoading} onChange={(e) => { setReferralSource(e.target.value); if (referralSourceError) setReferralSourceError(""); }}>
                  <option value="">{t("Pilih referral source")}</option>
                  {referralSources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {referralSourceError && <div className="lms-error-msg">{referralSourceError}</div>}
              </div>
              {contacts.map((contact, idx) => (
                <div key={idx} className="lms-repeater-card">
                  <div className="lms-repeater-card-header">
                    <span className="t-h3">Contact #{idx + 1}</span>
                    {contacts.length > 1 && (
                      <button className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon" style={{ color: "var(--danger-500)" }} onClick={() => { setContacts(contacts.filter((_, i) => i !== idx)); setContactErrors(contactErrors.filter((_, i) => i !== idx)); }}>
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                  <div className={`lms-form-group ${contactErrors[idx]?.name ? "has-error" : ""}`} style={{ marginBottom: 12 }}>
                    <label className="lms-label">{t("Nama")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                    <input className="lms-input" value={contact.name} onChange={(e) => { const n = [...contacts]; n[idx].name = e.target.value; setContacts(n); }} placeholder={t("Masukkan nama")} />
                    {contactErrors[idx]?.name && <div className="lms-error-msg">{contactErrors[idx].name}</div>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className={`lms-form-group ${contactErrors[idx]?.email ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Email")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input className="lms-input" type="email" value={contact.email} onChange={(e) => { const n = [...contacts]; n[idx].email = e.target.value; setContacts(n); }} placeholder={t("Masukkan email")} />
                      {contactErrors[idx]?.email && <div className="lms-error-msg">{contactErrors[idx].email}</div>}
                    </div>
                    <div className={`lms-form-group ${contactErrors[idx]?.phone ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Phone")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input className="lms-input" maxLength={13} value={contact.phone} onChange={(e) => { const n = [...contacts]; n[idx].phone = e.target.value; setContacts(n); }} placeholder={t("Masukkan nomor telepon")} />
                      {contactErrors[idx]?.phone && <div className="lms-error-msg">{contactErrors[idx].phone}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 4: Package Selection */}
          {step === 4 && (
            <div>
              <div className="t-h2" style={{ marginBottom: 20 }}>{t("Package Selection")}</div>
              <div className="lms-step4-grid">
                <PackageSelectList packages={packages} packagesLoading={packagesLoading} subscription={subscription} subscriptionErrors={subscriptionErrors} onChange={handleSubscriptionChange} />
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className={`lms-form-group ${subscriptionErrors.tanggalMulai ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Start Date")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input className="lms-input" type="date" value={subscription.tanggalMulai} onChange={(e) => setSubscription({ ...subscription, tanggalMulai: e.target.value })} />
                      {subscriptionErrors.tanggalMulai && <div className="lms-error-msg">{subscriptionErrors.tanggalMulai}</div>}
                    </div>
                    <div className={`lms-form-group ${subscriptionErrors.tanggalBerakhir ? "has-error" : ""}`}>
                      <label className="lms-label">{t("End Date")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input className="lms-input" type="date" value={subscription.tanggalBerakhir} onChange={(e) => setSubscription({ ...subscription, tanggalBerakhir: e.target.value })} />
                      {subscriptionErrors.tanggalBerakhir && <div className="lms-error-msg">{subscriptionErrors.tanggalBerakhir}</div>}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="lms-form-group">
                      <label className="lms-label">{t("Grace Period (days)")}</label>
                      <input className="lms-input" type="number" min={0} value={subscription.gracePeriod} onChange={(e) => setSubscription({ ...subscription, gracePeriod: parseInt(e.target.value) || 0 })} />
                    </div>
                    <div className={`lms-form-group ${subscriptionErrors.discountPercent ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Discount (%)")}</label>
                      <input className="lms-input" type="number" min={0} max={100} value={subscription.discountPercent} onChange={(e) => setSubscription({ ...subscription, discountPercent: Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100) })} />
                      {subscriptionErrors.discountPercent && <div className="lms-error-msg">{subscriptionErrors.discountPercent}</div>}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="lms-form-group">
                      <label className="lms-label">{t("Jumlah Student")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input className="lms-input" type="number" min={1} value={subscription.jumlahStudent} onChange={(e) => setSubscription({ ...subscription, jumlahStudent: Math.max(parseInt(e.target.value) || 1, 1) })} />
                    </div>
                    <div className="lms-form-group">
                      <label className="lms-label">{t("Installation Fee")}</label>
                      <input className="lms-input" type="number" min={0} value={subscription.installationFee} onChange={(e) => setSubscription({ ...subscription, installationFee: parseInt(e.target.value) || 0 })} />
                    </div>
                  </div>
                  <div className="lms-form-group">
                    <div className="lms-label" style={{ marginBottom: 6 }}>
                      {t("Additional Attachment")} <span style={{ fontWeight: 400, textTransform: "none", fontSize: 11 }}>{t("(opsional)")}</span>
                    </div>
                    <label
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        gap: 8, border: "1.5px dashed var(--border-strong)", borderRadius: 10,
                        padding: "20px 16px", cursor: "pointer", background: "var(--slate-50)",
                        transition: "border-color 0.15s, background 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand-400)"; e.currentTarget.style.background = "var(--brand-50)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.background = "var(--slate-50)"; }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28" style={{ color: "var(--text-muted)" }}>
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      {subscription.additionalAttachment ? (
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--success-700)" }}>{subscription.additionalAttachment.name}</div>
                           <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{t("Klik untuk ganti file")}</div>
                        </div>
                      ) : (
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-base)" }}>{t("Klik untuk upload file")}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{t("PDF, DOC, DOCX, atau gambar")}</div>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => setSubscription({ ...subscription, additionalAttachment: e.target.files[0] })}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                  <div className="lms-price-summary">
                    <div className="lms-price-summary-title">{t("Price Summary")}</div>
                    <div className="lms-price-row"><span>{t("Package Price")}</span><span>Rp {basePrice.toLocaleString("id-ID")} / bulan</span></div>
                    <div className="lms-price-row"><span>{t("Jumlah Student")}</span><span>{jumlahStudent} student</span></div>
                    <div className="lms-price-row"><span>{t("Duration")}</span><span>{durationMonths} bulan</span></div>
                    {subscription.discountPercent > 0 && <div className="lms-price-row"><span>{t("Discount")} ({subscription.discountPercent}%)</span><span style={{ color: "var(--danger-500)" }}>-Rp {(basePrice * jumlahStudent * durationMonths - discountedPrice).toLocaleString("id-ID")}</span></div>}
                    <div className="lms-price-row"><span>{t("Installation Fee")}</span><span>Rp {(subscription.installationFee || 0).toLocaleString("id-ID")}</span></div>
                    <div className="lms-price-divider" />
                    <div className="lms-price-row lms-price-total"><span>{t("Total")}</span><span>Rp {totalPrice.toLocaleString("id-ID")}</span></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20 }}>
        <button className="lms-btn lms-btn-secondary" onClick={back} disabled={step === 1}>{t("Previous")}</button>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="lms-btn lms-btn-ghost" onClick={() => history.goBack()}>{t("Cancel")}</button>
          {step < 4 ? (
            <button className="lms-btn lms-btn-primary" onClick={next}>{t("Next")}</button>
          ) : (
            <button className="lms-btn lms-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <span className="lms-spinner" style={{ width: 14, height: 14 }} /> : <SaveIcon />}
              {saving ? t("Saving...") : t("Save Changes")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default EditTenant;
